import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import { ArrowLeft, FilmSlate } from "@phosphor-icons/react";
import { toEmbedUrl } from "@/components/onboarding/StepVideo";

const STEP_LABELS = {
  welcome: "1 · Welcome",
  business: "2 · Business basics",
  opening_cash: "3 · Opening cash",
  recurring_costs: "4 · Regular costs",
  one_off: "5 · One-off costs",
  personal_draw: "6 · Paying yourself",
  revenue: "7 · Income streams",
  seasonality: "8 · Your rhythm",
  goals: "9 · Your 12-month plans",
  nearly_there: "10 · Nearly there",
};

export default function AdminSetupVideosPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [videos, setVideos] = useState({});
  const [saving, setSaving] = useState(false);
  const [rhythm, setRhythm] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    axios.get(`${API}/admin/rhythm-usage`).then((r) => setRhythm(r.data.counts || {})).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user?.is_admin) return;
    axios.get(`${API}/onboarding/videos`).then((r) => setVideos(r.data.videos || {})).catch(() => toast.error("Could not load videos"));
  }, [user]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/onboarding-videos`, { videos });
      setVideos(r.data.videos);
      toast.success("Setup videos saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-setup-videos-page">
      <button type="button" className="btn-secondary" onClick={() => nav("/admin")} data-testid="setup-videos-back-btn">
        <ArrowLeft size={14} weight="bold" /> Back to Admin
      </button>
      <div>
        <div className="section-eyebrow flex items-center gap-2">
          <FilmSlate size={14} weight="duotone" /> Build your forecast
        </div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">Setup videos</h1>
        <p className="mt-3 text-black/70 max-w-2xl">
          Paste a Loom share link for any step (YouTube and Vimeo work too). Steps without a link simply don't show a video.
        </p>
      </div>
      <div className="card space-y-4">
        {Object.entries(STEP_LABELS).map(([k, label]) => {
          const v = videos[k] || "";
          const bad = v && !toEmbedUrl(v);
          return (
            <div key={k}>
              <label className="block text-xs font-semibold mb-1">{label}</label>
              <input
                className="input"
                value={v}
                placeholder="https://www.loom.com/share/…"
                onChange={(e) => setVideos((s) => ({ ...s, [k]: e.target.value }))}
                data-testid={`setup-video-input-${k}`}
              />
              {bad && <p className="text-xs text-red-700 mt-1">That doesn't look like a Loom, YouTube or Vimeo link.</p>}
            </div>
          );
        })}
        <button type="button" className="btn-primary" onClick={save} disabled={saving} data-testid="setup-videos-save-btn">
          {saving ? "Saving…" : "Save videos"}
        </button>
      </div>
      {rhythm && (
        <div className="card" data-testid="rhythm-usage-card">
          <div className="section-eyebrow mb-3">Step 8 · Your rhythm: who picks what</div>
          {Object.keys(rhythm).length === 0 ? (
            <p className="text-sm text-black/60">No forecasts built with the new setup yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {Object.entries(rhythm).map(([k, n]) => (
                <li key={k} data-testid={`rhythm-usage-${k}`}>
                  <strong>{k === "steady" ? "Pretty steady (skipped / default)" : k.replace(/_/g, " ")}</strong>: {n}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
